import { getFileUrl } from '@/clients/http'
import { Image as ImageIcon } from 'lucide-react'

import { useBusiness } from '@/contexts/BusinessContext'

import { ProductModel } from '@/types/model/Product'

interface ProductOverviewTabProps {
  product: ProductModel.Product
  minPrice: number
  maxPrice: number
  totalStock: number
}

export const ProductOverviewTab = ({
  product,
  minPrice,
  maxPrice,
  totalStock,
}: ProductOverviewTabProps) => {
  const { formatCurrency } = useBusiness()

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <div className="flex aspect-square items-center justify-center overflow-hidden rounded-lg border border-border bg-subdued">
        {product.thumbnail_id?.[0]?.path ? (
          <img
            src={getFileUrl(product.thumbnail_id?.[0]?.path)}
            alt={product.name}
            className="size-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <ImageIcon className="size-8" />
            <span className="text-sm">No image available</span>
          </div>
        )}
      </div>

      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1 rounded-lg border border-border p-3">
            <p className="text-xs font-medium text-text-2">Price range</p>
            <p className="text-heading-md tabular-nums text-ink">
              {formatCurrency(minPrice)} {maxPrice > minPrice && `– ${formatCurrency(maxPrice)}`}
            </p>
          </div>
          <div className="space-y-1 rounded-lg border border-border p-3">
            <p className="text-xs font-medium text-text-2">Total stock</p>
            <p className="text-heading-md tabular-nums text-ink">{totalStock} units</p>
          </div>
        </div>

        <dl className="divide-y divide-border-subtle">
          <div className="flex items-center justify-between gap-4 py-2.5">
            <dt className="text-text-2">Category</dt>
            <dd className="font-medium text-ink">{(product as any).category?.name || 'Uncategorized'}</dd>
          </div>
          <div className="flex items-center justify-between gap-4 py-2.5">
            <dt className="text-text-2">Brand</dt>
            <dd className="font-medium text-ink">{(product as any).brand?.name || 'Generic'}</dd>
          </div>
          <div className="flex items-center justify-between gap-4 py-2.5">
            <dt className="text-text-2">Discount</dt>
            <dd className="font-medium tabular-nums text-ink">
              {product.discount
                ? product.discount_type === 'percentage'
                  ? `${product.discount}% off`
                  : `${formatCurrency(Number(product.discount))} off`
                : 'No discount'}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-4 py-2.5">
            <dt className="text-text-2">Slug</dt>
            <dd className="truncate font-mono text-xs text-muted-foreground">{product.slug}</dd>
          </div>
        </dl>
      </div>
    </div>
  )
}
